import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { Stack, Typography } from 'investira.react.components';
import SearchAtivos from '../../../components/organisms/SearchAtivos';
import SortButton from '../../../components/molecules/SortButton';
import { ROUTES_MERCADOS } from '../../../enums';
import { getAtivosFilterConfig } from '../../../utils/ativosFilterRegistry';
import {
  readAtivosFilterQuery,
  removePaginationFromPath,
  serializeAtivosFilterQuery,
  updateAtivosFilterQuery
} from '../_shared/ativosFilterQuery';

/**
 * Compõe a busca e a ordenação exibidas abaixo do título das páginas de mercado.
 *
 * @param {Object} pProps Propriedades recebidas pela área de termos da listagem.
 * @param {Function} pProps.dataRead Consulta configurada pela página de mercado.
 * @param {boolean} pProps.pageIsLoading Indica se a página está carregando os ativos.
 * @param {number} pProps.totalResults Quantidade total de ativos encontrados.
 * @returns {JSX.Element} Busca seguida pela quantidade de resultados.
 */
const AtivosMercadoTerms = memo(pProps => {
  const xLocation = useLocation();
  const xNavigate = useNavigate();
  const xPathnameBase = xLocation.pathname.split('/')[2];
  const xMercado = ROUTES_MERCADOS[xPathnameBase];
  const xConfig = getAtivosFilterConfig(xMercado?.id);
  const xMercadoDescricao = xMercado?.descricao || 'ativos';
  const xQuery = readAtivosFilterQuery(xLocation.search, xMercado?.id);
  const xSearchParams = { ...xQuery };
  const xPlaceholder = `Pesquisar por ${xMercadoDescricao.toLocaleLowerCase('pt-BR')}`;
  const xResultsPath = `/ativos/${xPathnameBase}`;

  // A busca rápida usa os filtros aplicados, mas mantém pesquisa e ordenação próprias.
  delete xSearchParams.pesquisa;
  delete xSearchParams.sort;

  /** Aplica a ordenação imediatamente, preservando os filtros e voltando à primeira página. */
  function pvChangeSort(pSort) {
    const xNextQuery = updateAtivosFilterQuery(xQuery, { sort: pSort || null }, xConfig.mercado);
    const xSearch = serializeAtivosFilterQuery(xNextQuery, xConfig.mercado);
    const xPathname = removePaginationFromPath(xLocation.pathname);

    xNavigate(`${xPathname}${xSearch}`);
  }

  return (
    <Stack spacing={2} mb={4}>
      <SearchAtivos
        dataRead={pProps.dataRead}
        dataReadParams={xSearchParams}
        mercado={xMercado?.id}
        pageIsLoading={pProps.pageIsLoading}
        placeholder={xPlaceholder}
        preserveQuery={true}
        resultsPath={xResultsPath}
        searchValue={xQuery.pesquisa || ''}
      />
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        spacing={2}>
        <Typography variant="caption" color="textPrimary" sx={{ fontWeight: 600 }}>
          {pProps.totalResults} resultados
        </Typography>
        {xConfig && (
          <Stack sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 280 } }}>
            <SortButton
              id="ativos-mercado-sort"
              ariaLabel="Ordenar ativos"
              value={xQuery.sort || ''}
              options={xConfig.sortOptions}
              groups={xConfig.sortGroups}
              onChange={pvChangeSort}
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
});

AtivosMercadoTerms.propTypes = {
  dataRead: PropTypes.func,
  pageIsLoading: PropTypes.bool,
  totalResults: PropTypes.number
};

AtivosMercadoTerms.defaultProps = {
  dataRead: undefined,
  pageIsLoading: false,
  totalResults: 0
};

AtivosMercadoTerms.displayName = 'AtivosMercadoTerms';

export default AtivosMercadoTerms;
